/**
 * Parse an amount as a Uruguayan professional types it.
 *
 * `Number("1.500,00")` is NaN and `Number("1.500")` is 1.5 — both wrong for a
 * country that writes thousands with `.` and decimals with `,`. A native
 * `<input type="number">` does not save us either: it rejects the comma
 * keystroke outright, so the field looks filled while its value is "".
 * Every amount a professional types goes through here instead.
 *
 * Rules, in order:
 *  - Blank (or nothing but separators/whitespace) → null.
 *  - BOTH separators present → the LAST one is the decimal mark, the other is
 *    grouping. `1.500,25` → 1500.25 and `1,500.25` → 1500.25.
 *  - Only `,` → the decimal mark. `1500,25` → 1500.25.
 *  - Only `.` → grouping when `allowThousandsGrouping` and every group after a
 *    dot is exactly 3 digits (`1.500` → 1500, `1.500.000` → 1500000);
 *    otherwise the decimal mark (`1.5` → 1.5).
 *
 * `allowThousandsGrouping` is the caller's knowledge of what the field means,
 * not a preference: in pesos `1.500` is fifteen hundred, while as a quantity
 * of UR `2.5` is two and a half units. The quote form passes true for a UYU
 * line and false for a UR one.
 *
 * Returns null for anything that is not a finite number — the caller decides
 * whether that is an error to show or a field still being typed.
 */
export function parseUyAmount(
  raw: string,
  { allowThousandsGrouping = true }: { allowThousandsGrouping?: boolean } = {},
): number | null {
  // Non-breaking spaces included: pasting from Word or a PDF brings them along.
  const trimmed = raw.replace(/[\s\u00a0\u202f]/g, "");
  if (trimmed.length === 0) return null;
  // A leading sign is accepted so a negative reads as negative rather than as
  // unparseable — the caller rejects it on value, with its own message.
  if (!/^[+-]?[\d.,]+$/.test(trimmed)) return null;

  const sign = trimmed.startsWith("-") ? -1 : 1;
  const digitsAndSeparators = trimmed.replace(/^[+-]/, "");

  const lastComma = digitsAndSeparators.lastIndexOf(",");
  const lastDot = digitsAndSeparators.lastIndexOf(".");

  let decimalAt: number;
  if (lastComma >= 0 && lastDot >= 0) {
    decimalAt = Math.max(lastComma, lastDot);
  } else if (lastComma >= 0) {
    decimalAt = lastComma;
  } else if (lastDot >= 0) {
    decimalAt = allowThousandsGrouping && isGroupedThousands(digitsAndSeparators) ? -1 : lastDot;
  } else {
    decimalAt = -1;
  }

  const integerPart = (
    decimalAt < 0 ? digitsAndSeparators : digitsAndSeparators.slice(0, decimalAt)
  ).replace(/[.,]/g, "");
  const fractionPart =
    decimalAt < 0 ? "" : digitsAndSeparators.slice(decimalAt + 1).replace(/[.,]/g, "");

  if (integerPart.length === 0 && fractionPart.length === 0) return null;

  const value = Number(`${integerPart || "0"}.${fractionPart || "0"}`);
  return Number.isFinite(value) ? sign * value : null;
}

/** `1.500` / `1.500.000` — dots only, and every group after one is 3 digits. */
function isGroupedThousands(value: string): boolean {
  return /^\d{1,3}(\.\d{3})+$/.test(value);
}
